/**
 * Sticky Note Service
 *
 * Handles API calls for managing sticky notes on Timeline 2.0.
 * Sticky notes can be attached to components or positioned between them.
 */

import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../utils/api';

/**
 * List sticky notes for a trip
 * @param {string} tripId - Trip ID
 * @returns {Promise<Array>} List of sticky notes
 */
export async function listStickyNotes(tripId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/trips/${tripId}/sticky-notes`,
    { headers }
  );

  return response.data;
}

/**
 * List sticky notes for several trips in one request
 * @param {string[]} tripIds - Trip IDs
 * @returns {Promise<Object>} Map of trip ID to its list of sticky notes
 */
export async function listStickyNotesForTrips(tripIds) {
  const ids = (tripIds || []).filter(Boolean);
  if (ids.length === 0) return {};
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.get(
    `${apiBase}/sticky-notes`,
    { headers, params: { trip_ids: ids.join(',') } }
  );

  return response.data || {};
}

/**
 * Create a new sticky note
 * @param {string} tripId - Trip ID
 * @param {Object} note - Note data
 * @param {string} note.headline - Short headline text
 * @param {string} [note.content=''] - Full content text
 * @param {boolean} [note.is_confidential=false] - Whether note is private
 * @param {Object} note.position - Position data
 * @param {string} note.position.position_type - 'attached' | 'between' | 'start' | 'end' | 'inside'
 * @param {string} [note.position.attached_to_component_id] - Component ID for attached/inside notes
 * @param {string} [note.position.anchor_before_id] - Component before (for between)
 * @param {string} [note.position.anchor_after_id] - Component after (for between)
 * @param {number} [note.position.rank=0] - Order rank for multiple notes at same position
 * @param {number} [note.position.offset_x=0] - X offset % within component (for inside type)
 * @param {number} [note.position.offset_y=0] - Y offset % within component (for inside type)
 * @returns {Promise<Object>} Created sticky note
 */
export async function createStickyNote(tripId, note) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.post(
    `${apiBase}/trips/${tripId}/sticky-notes`,
    note,
    { headers }
  );

  return response.data;
}

/**
 * Create a sticky note as an executive from the ExecDashboard.
 * Allowed even when the executive has no read/write access to the trip,
 * provided the trip lists them in executive_ids. The backend fans out an
 * email + in-app notification to the exec's EAs.
 * @param {string} tripId - Trip ID
 * @param {Object} note - Note data (same shape as createStickyNote)
 * @returns {Promise<Object>} Created sticky note
 */
export async function createExecStickyNote(tripId, note) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.post(
    `${apiBase}/trips/${tripId}/sticky-notes/exec-note`,
    note,
    { headers }
  );

  return response.data;
}

/**
 * Update a sticky note
 * @param {string} noteId - Note ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.headline] - Short headline text
 * @param {string} [updates.content] - Full content text
 * @param {boolean} [updates.is_expanded] - Whether note is expanded
 * @param {boolean} [updates.is_confidential] - Whether note is private
 * @param {Object} [updates.position] - Position data (for repositioning)
 * @returns {Promise<Object>} Updated sticky note
 */
export async function updateStickyNote(noteId, updates) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.put(
    `${apiBase}/sticky-notes/${noteId}`,
    updates,
    { headers }
  );

  return response.data;
}

/**
 * Delete a sticky note
 * @param {string} noteId - Note ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteStickyNote(noteId) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const response = await axios.delete(
    `${apiBase}/sticky-notes/${noteId}`,
    { headers }
  );

  return response.data;
}

/**
 * Toggle note expansion state
 * @param {string} noteId - Note ID
 * @param {boolean} isExpanded - New expansion state
 * @returns {Promise<Object>} Updated sticky note
 */
export async function toggleNoteExpanded(noteId, isExpanded) {
  return updateStickyNote(noteId, { is_expanded: isExpanded });
}

/**
 * Update note position (for drag and drop)
 * @param {string} noteId - Note ID
 * @param {Object} position - New position data
 * @returns {Promise<Object>} Updated sticky note
 */
export async function updateNotePosition(noteId, position) {
  return updateStickyNote(noteId, { position });
}

/**
 * Transcribe audio to text using speech-to-text
 * @param {Blob} audioBlob - Audio blob from recording
 * @returns {Promise<Object>} Transcription result { success, text }
 */
export async function transcribeAudio(audioBlob) {
  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  // Remove Content-Type from headers as FormData sets it automatically
  delete headers['Content-Type'];

  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  const response = await axios.post(
    `${apiBase}/transcribe-audio`,
    formData,
    { headers }
  );

  return response.data;
}
